import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subject, takeUntil, timer } from 'rxjs';
import { ConfirmActionModalComponent } from '../confirm-action-modal/confirm-action-modal.component';
import { AddCardModalComponent, AddedCard } from '../add-card-modal/add-card-modal.component';
import {
  ImportedCardList,
  PaymentMethodErrorCode,
  SavedCard,
  SavedCardList,
  SubscriptionPaymentMethodService,
} from '../../../services/subscription-payment-method.service';
import { ApiResponse } from '../../types';
import { ToastService } from '../../../services/toast.service';
import {
  cardBadge,
  cardExpiryLabel,
  cardLabel,
  importedPaymentsNotice,
  paymentMethodErrorCopy,
} from '../../utils/payment-method.copy';

/** The membership the pop-up changes the renewal card for. */
export interface PaymentMethodSubject {
  subscriptionId: string;
  planName: string;
  plate: string;
  nextRenewal: string | null;
  price: string | number | null;
  /**
   * An imported membership: the vendor its payments go through ("DRB"). The pop-up says so, lists
   * the cards saved for it and adds one, but none is chosen: the vendor keeps billing it.
   */
  billedBy?: string | null;
}

/** How long Add New Card stays up under its "New card added" toast before the list returns. */
export const CARD_ADDED_PAUSE_MS = 1500;

/**
 * CWS-1536 "Payment method": choose the card a membership renews on, from the customer's saved
 * cards, or add a new one (AddCardModalComponent). Save is offered only for a different card
 * that has not expired; the change applies from the next renewal. An imported membership
 * (`subject.billedBy`) gets a notice that its payments go through its vendor, its saved cards and
 * Add New Card: a card is only saved for it, never charged or chosen here.
 *
 * @selector app-payment-method-modal
 * @input subject - The membership.
 * @output updated - The renewal card was changed; carries the new card.
 * @output closed - Closed without a change.
 */
@Component({
  selector: 'app-payment-method-modal',
  standalone: true,
  imports: [CommonModule, ConfirmActionModalComponent, AddCardModalComponent],
  templateUrl: './payment-method-modal.component.html',
  styleUrls: ['./payment-method-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PaymentMethodModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) subject!: PaymentMethodSubject;

  @Output() updated = new EventEmitter<SavedCard>();
  @Output() closed = new EventEmitter<void>();

  step: 'select' | 'add' = 'select';

  loading = true;
  loadError = '';
  /** Why this membership's card cannot be changed; no cards are shown with it. */
  refusal = '';
  cards: SavedCard[] = [];
  selectedId: string | null = null;

  saving = false;
  saveError = '';
  /** The card last added here: it is selected, and Save makes it the renewal card. */
  addedCardId: string | null = null;
  /** That card was already on file: Add New Card found the original and removed the copy. */
  addedWasOnFile = false;
  /** The current card is only the customer's default: Save sets it on this membership. */
  renewalCardInherited = false;

  readonly cardLabel = cardLabel;
  readonly cardBadge = cardBadge;
  readonly cardExpiryLabel = cardExpiryLabel;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly paymentMethods: SubscriptionPaymentMethodService,
    private readonly toast: ToastService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  /** An imported membership: its vendor bills it, so cards are only saved for it. */
  get imported(): boolean {
    return !!this.subject.billedBy;
  }

  /** "Current transactions for this membership are processed through DRB. …", or '' for Way. */
  get importedNotice(): string {
    return this.subject.billedBy ? importedPaymentsNotice(this.subject.billedBy) : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentCard(): SavedCard | null {
    return this.cards.find(card => card.is_current) ?? null;
  }

  get selectedCard(): SavedCard | null {
    return this.cards.find(card => card.id === this.selectedId) ?? null;
  }

  /** Cards can be chosen or added: the list loaded and the membership is eligible. */
  get ready(): boolean {
    return !this.loading && !this.loadError && !this.refusal;
  }

  get canSave(): boolean {
    const selected = this.selectedCard;
    return (
      this.ready &&
      !this.saving &&
      !!selected &&
      !selected.is_expired &&
      (selected.id !== this.currentCard?.id || this.renewalCardInherited)
    );
  }

  select(card: SavedCard): void {
    if (card.is_expired || this.saving) {
      return;
    }
    this.selectedId = card.id;
    this.saveError = '';
  }

  /**
   * Loads the saved cards. Selects `preselectId` when given (a card just added), else the
   * current renewal card. `loaded` runs once the answer is in, whatever it was.
   */
  load(preselectId: string | null = null, loaded?: () => void): void {
    this.loading = true;
    this.loadError = '';
    this.refusal = '';
    this.listRequest()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.showList(response?.data, preselectId);
          loaded?.();
        },
        error: (code: PaymentMethodErrorCode) => {
          this.loading = false;
          this.loadError = paymentMethodErrorCopy(code);
          loaded?.();
        },
      });
  }

  private listRequest(): Observable<ApiResponse<SavedCardList | ImportedCardList>> {
    return this.imported
      ? this.paymentMethods.listImportedCards(this.subject.subscriptionId)
      : this.paymentMethods.listCards(this.subject.subscriptionId);
  }

  openAddCard(): void {
    this.saveError = '';
    this.step = 'add';
  }

  backFromAddCard(): void {
    this.step = 'select';
  }

  /**
   * Confirms the new card with a toast while Add New Card is still up, then returns to the list
   * with it selected. The toast names the card, so it waits for the list: Stripe's fields never
   * hand the digits to us. The current card stays the renewal card until Save.
   */
  onCardAdded({ paymentMethodId, duplicate, unfinished }: AddedCard): void {
    this.addedCardId = paymentMethodId;
    this.addedWasOnFile = duplicate;
    this.load(paymentMethodId, () => {
      if (unfinished) {
        this.placeUnfinishedCard(paymentMethodId);
      }
      this.announceAddedCard();
    });
  }

  /**
   * svc-order could not check the new card against the cards on file. The list hides a copy only
   * when another copy is the renewal card (otherwise it keeps the newest, the new one), so a new
   * card missing from the list is the renewal card typed again.
   */
  private placeUnfinishedCard(paymentMethodId: string): void {
    if (!this.ready || this.cards.some(card => card.id === paymentMethodId)) {
      return;
    }
    this.addedWasOnFile = true;
    this.addedCardId = this.currentCard?.id ?? null;
  }

  /** The note over the list after Add New Card, by what came of it. */
  get addedNotice(): string {
    if (this.imported) {
      return this.addedWasOnFile ? 'That card was already on file.' : 'Card saved.';
    }
    if (!this.addedWasOnFile) {
      return 'Card saved. Save to use it for the next renewal.';
    }
    return this.currentCard?.id === this.addedCardId
      ? 'That card was already on file, and it is the renewal card.'
      : 'That card was already on file. Save to use it for the next renewal.';
  }

  save(): void {
    const selected = this.selectedCard;
    if (!this.canSave || !selected) {
      return;
    }
    this.saving = true;
    this.saveError = '';
    this.paymentMethods
      .setRenewalCard(this.subject.subscriptionId, selected.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.saving = false;
          this.updated.emit(response?.data?.card ?? { ...selected, is_current: true });
        },
        error: (code: PaymentMethodErrorCode) => {
          this.saving = false;
          this.saveError = paymentMethodErrorCopy(code);
        },
      });
  }

  private showList(
    list: SavedCardList | ImportedCardList | undefined,
    preselectId: string | null
  ): void {
    this.loading = false;
    this.renewalCardInherited =
      list?.eligible === true &&
      'renewal_card_inherited' in list &&
      list.renewal_card_inherited === true;
    if (!list?.eligible) {
      this.cards = [];
      this.refusal = paymentMethodErrorCopy(list?.reason ?? 'UNKNOWN');
      return;
    }
    this.cards = list.cards;
    const preselect = this.cards.find(card => card.id === preselectId && !card.is_expired);
    this.selectedId = preselect?.id ?? this.currentCard?.id ?? null;
  }

  /**
   * "New card added", or "Card already added" for a card that was on file, with its digits when
   * the list has it; then back to the list.
   */
  private announceAddedCard(): void {
    const added = this.cards.find(card => card.id === this.addedCardId);
    const label = added ? cardLabel(added) : undefined;
    if (this.addedWasOnFile) {
      this.toast.showInfo('Card already added', label);
    } else {
      this.toast.showSuccess('New card added', label);
    }
    timer(CARD_ADDED_PAUSE_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.step = 'select'));
  }
}
